import { useRef, useState } from 'react'
import { Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormGroup } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import {
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import {
  normalizeCascadeReminders,
  normalizeExpectedEntities,
  normalizeOffsetReminders,
  normalizeReminderDuration,
} from './assignmentTimeline'
import AssignmentReminderTimingFields from './AssignmentReminderTimingFields'
import AssignmentDependencySelect from './AssignmentDependencySelect'
import { AssignmentDependencyOption } from './assignmentDependencies'
import Card from 'app/frontend/base/components/Card'
import { RaidPlanAssignmentDependency } from 'app/frontend/types/custom/RaidPlan'

export type NoteSettingsValue = {
  message?: string
  ttsMessage?: string
  expectedEntities?: number
  rgb?: string
  cascadeReminders?: number
  offsetReminders?: number
  reminderDuration?: number
  dependsOn?: RaidPlanAssignmentDependency
}

type Props = {
  label?: string
  dependsOn?: RaidPlanAssignmentDependency
  abilityDependsOn?: RaidPlanAssignmentDependency
  dependencyOptions: AssignmentDependencyOption[]
  allDependencyOptions: AssignmentDependencyOption[]
  message?: string
  ttsMessage?: string
  expectedEntities?: number
  rgb?: string
  cascadeReminders?: number
  offsetReminders?: number
  reminderDuration?: number
  abilityCascadeReminders?: number
  abilityOffsetReminders?: number
  abilityReminderDuration?: number
  messagePlaceholder: string
  ttsPlaceholder: string
  expectedEntitiesPlaceholder?: number
  rgbPlaceholder?: string
  canEdit: boolean
  useNoteLabel?: boolean
  onSave: (next: NoteSettingsValue) => void
  onOpenChange?: (open: boolean) => void
  onEditForAllReminders?: () => void
}

const AssignmentNoteSettingsDialog = ({
  label,
  dependsOn,
  abilityDependsOn,
  dependencyOptions,
  allDependencyOptions,
  message,
  ttsMessage,
  expectedEntities,
  rgb,
  cascadeReminders,
  offsetReminders,
  reminderDuration,
  abilityCascadeReminders,
  abilityOffsetReminders,
  abilityReminderDuration,
  messagePlaceholder,
  ttsPlaceholder,
  expectedEntitiesPlaceholder,
  rgbPlaceholder,
  canEdit,
  useNoteLabel = false,
  onSave,
  onOpenChange,
  onEditForAllReminders,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [messageDraft, setMessageDraft] = useState(message ?? '')
  const [ttsMessageDraft, setTtsMessageDraft] = useState(ttsMessage ?? '')
  const [expectedEntitiesDraft, setExpectedEntitiesDraft] = useState(
    expectedEntities?.toString() ?? '',
  )
  const [cascadeDraft, setCascadeDraft] = useState(
    cascadeReminders?.toString() ?? '',
  )
  const [offsetDraft, setOffsetDraft] = useState(
    offsetReminders?.toString() ?? '',
  )
  const [durationDraft, setDurationDraft] = useState(
    reminderDuration?.toString() ?? '',
  )
  const [rgbDraft, setRgbDraft] = useState(rgb ?? '')
  const rgbDraftRef = useRef(rgb ?? '')
  const colorPickerOpenRef = useRef(false)

  const setRgbDraftValue = (next: string) => {
    rgbDraftRef.current = next
    setRgbDraft(next)
  }

  const resetDrafts = () => {
    setMessageDraft(message ?? '')
    setTtsMessageDraft(ttsMessage ?? '')
    setExpectedEntitiesDraft(expectedEntities?.toString() ?? '')
    setCascadeDraft(cascadeReminders?.toString() ?? '')
    setOffsetDraft(offsetReminders?.toString() ?? '')
    setDurationDraft(reminderDuration?.toString() ?? '')
    setRgbDraftValue(rgb ?? '')
  }

  const colorValue =
    rgbDraft.trim() || rgbPlaceholder?.trim() || 'rgb(255, 255, 255)'

  const commit = (
    overrides: Pick<NoteSettingsValue, 'dependsOn'> = { dependsOn },
  ) => {
    const nextMessage = messageDraft.trim()
    const nextTtsMessage = ttsMessageDraft.trim()
    const trimmedRgb = rgbDraftRef.current.trim()
    onSave({
      message: nextMessage.length ? nextMessage : undefined,
      ttsMessage: nextTtsMessage.length ? nextTtsMessage : undefined,
      expectedEntities: normalizeExpectedEntities(expectedEntitiesDraft),
      rgb: trimmedRgb.length ? trimmedRgb : undefined,
      cascadeReminders: normalizeCascadeReminders(cascadeDraft),
      offsetReminders: normalizeOffsetReminders(offsetDraft),
      reminderDuration: normalizeReminderDuration(durationDraft),
      dependsOn: overrides.dependsOn,
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) resetDrafts()
    else commit()
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  const handleEditForAllReminders = () => {
    handleOpenChange(false)
    window.setTimeout(() => {
      onEditForAllReminders?.()
    }, 0)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='custom'
          className='h-7 w-7 p-0'
        >
          <Settings2Icon className='h-3.5 w-3.5' />
        </Button>
      </DialogTrigger>
      <DialogContent className='w-[64rem] max-w-[calc(100vw-2rem)]'>
        <DialogHeader>
          <DialogTitle>
            {label
              ? t('dashboard.team.plans.assignments.note-settings-for', {
                  label,
                })
              : t('dashboard.team.plans.assignments.note-settings')}
          </DialogTitle>
          <DialogDescription asChild>
            <div className='flex flex-wrap items-center gap-2'>
              <span>
                {t(
                  'dashboard.team.plans.assignments.note-settings-override-description',
                )}
              </span>
              {onEditForAllReminders ? (
                <Button
                  type='button'
                  variant='outline'
                  size='xs'
                  onClick={handleEditForAllReminders}
                >
                  {t('dashboard.team.plans.assignments.edit-for-all-reminders')}
                </Button>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-1.5'>
          <div className='text-sm font-medium'>
            {t('dashboard.team.plans.assignments.reminder-settings')}
          </div>
          <Card className='p-3'>
            <FormGroup className='flex-wrap items-end'>
              <div className='grow'>
                <Label>
                  {t(
                    useNoteLabel
                      ? 'dashboard.team.plans.assignments.note-label'
                      : 'dashboard.team.plans.assignments.note-options',
                  )}
                </Label>
                <Input
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  onBlur={() => commit()}
                  className='h-8'
                  placeholder={messagePlaceholder}
                  disabled={!canEdit}
                />
              </div>

              <div className='grow'>
                <Label>
                  {t('dashboard.team.plans.assignments.note-tts-options')}
                </Label>
                <Input
                  value={ttsMessageDraft}
                  onChange={(event) => setTtsMessageDraft(event.target.value)}
                  onBlur={() => commit()}
                  className='h-8'
                  placeholder={messageDraft.trim() || ttsPlaceholder}
                  disabled={!canEdit || useNoteLabel}
                />
              </div>

              <AssignmentReminderTimingFields
                cascadeValue={cascadeDraft}
                offsetValue={offsetDraft}
                durationValue={durationDraft}
                cascadePlaceholder={abilityCascadeReminders?.toString()}
                offsetPlaceholder={abilityOffsetReminders?.toString()}
                durationPlaceholder={abilityReminderDuration?.toString()}
                canEdit={canEdit}
                onCascadeChange={setCascadeDraft}
                onOffsetChange={setOffsetDraft}
                onDurationChange={setDurationDraft}
                onBlur={() => commit()}
              />

              <div className='flex shrink-0 flex-col justify-end'>
                <Label className='flex h-5 items-center'>
                  {t('dashboard.team.plans.assignments.color')}
                </Label>
                <ColorPicker
                  value={colorValue}
                  format='rgb'
                  className='flex'
                  onValueChange={(value) => {
                    if (!colorPickerOpenRef.current || !canEdit) return
                    if (!rgbDraft && value === colorValue) return
                    setRgbDraftValue(value)
                  }}
                  onOpenChange={(nextOpen) => {
                    colorPickerOpenRef.current = nextOpen
                    if (nextOpen || !canEdit) return
                    commit()
                  }}
                >
                  <ColorPickerTrigger asChild>
                    <Button
                      variant='ghost'
                      size='custom'
                      className='p-0'
                      disabled={!canEdit}
                    >
                      <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-18.5 h-8' />
                    </Button>
                  </ColorPickerTrigger>
                  <ColorPickerContent>
                    <ColorPickerArea />
                    <ColorPickerHueSlider />
                    <ColorPickerInput />
                  </ColorPickerContent>
                </ColorPicker>
              </div>
            </FormGroup>
          </Card>
        </div>

        <div className='space-y-1.5'>
          <div className='text-sm font-medium'>
            {t('dashboard.team.plans.assignments.assignment-settings')}
          </div>
          <Card className='p-3'>
            <FormGroup className='flex-wrap items-end'>
              <div className='w-40'>
                <Label>
                  {t('dashboard.team.plans.assignments.expected-player-count')}
                </Label>
                <Input
                  type='text'
                  inputMode='numeric'
                  maxLength={2}
                  value={expectedEntitiesDraft}
                  onChange={(event) =>
                    setExpectedEntitiesDraft(event.target.value)
                  }
                  onBlur={() => commit()}
                  className='h-8'
                  placeholder={expectedEntitiesPlaceholder?.toString()}
                  disabled={!canEdit}
                />
              </div>
              <AssignmentDependencySelect
                className='flex-1'
                value={dependsOn}
                inheritedValue={abilityDependsOn}
                options={dependencyOptions}
                allOptions={allDependencyOptions}
                disabled={!canEdit}
                onChange={(next) => commit({ dependsOn: next })}
              />
            </FormGroup>
          </Card>
        </div>

        <div className='justify-end flex gap-2'>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignmentNoteSettingsDialog
